import { types as sdkTypes } from '../../util/sdkLoader';
import { userLocation } from '../../util/maps';
import { getCachedGeocode, setCachedGeocode, normalizeCacheKey } from '../../util/geocodeCache';

const PLACE_PREDICTIONS_CACHE_NAMESPACE = 'placePredictions';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

export const CURRENT_LOCATION_ID = 'current-location';

// Half-width of the box generated around a city centre. 40 km used to leave hosts on the far side
// of a province out of their own province's search — a pool in Tui, 42.7 km from the centre of
// Pontevedra, never showed up in a "Pontevedra" search. See docs/search-seo.md §2.1.
const GENERATED_BOUNDS_DEFAULT_DISTANCE = 50000; // meters
// Distances for generated bounding boxes for different Mapbox place types
const PLACE_TYPE_BOUNDS_DISTANCES = {
  address: 2500,
  country: 2000,
  region: 2000,
  postcode: 50000,
  district: 2000,
  place: 50000,
  locality: 50000,
  neighborhood: 50000,
  poi: 50000,
  'poi.landmark': 50000,
};

const locationBounds = (latlng, distance) => {
  if (!latlng) {
    return null;
  }

  const bounds = new window.mapboxgl.LngLat(latlng.lng, latlng.lat).toBounds(distance);
  return new SDKLatLngBounds(
    new SDKLatLng(bounds.getNorth(), bounds.getEast()),
    new SDKLatLng(bounds.getSouth(), bounds.getWest())
  );
};

const placeOrigin = prediction => {
  if (prediction && Array.isArray(prediction.center) && prediction.center.length === 2) {
    // Coordinates in Mapbox features are represented as [longitude, latitude].
    return new SDKLatLng(prediction.center[1], prediction.center[0]);
  }
  return null;
};

const placeBounds = prediction => {
  if (prediction) {
    // if (Array.isArray(prediction.bbox) && prediction.bbox.length === 4) {
    //   // Bounds in Mapbox features are represented as [minX, minY, maxX, maxY]
    //   return new SDKLatLngBounds(
    //     new SDKLatLng(prediction.bbox[3], prediction.bbox[2]),
    //     new SDKLatLng(prediction.bbox[1], prediction.bbox[0])
    //   );
    // } else {
    // If bounds are not available, generate them around the origin

    // Resolve bounds distance based on place type
    const placeType = Array.isArray(prediction.place_type) && prediction.place_type[0];

    const distance =
      (placeType && PLACE_TYPE_BOUNDS_DISTANCES[placeType]) || GENERATED_BOUNDS_DEFAULT_DISTANCE;

    return locationBounds(placeOrigin(prediction), distance);
    // }
  }
  return null;
};

/** Mismos bounds que el buscador del header (p. ej. 40 km para ciudades `place`). */
export { placeBounds };

export const GeocoderAttribution = () => null;

// Clean province text: "provincia de Barcelona" -> "Barcelona", "Province of X" -> "X"
const cleanProvinceText = text => {
  if (!text || typeof text !== 'string') return text || '';
  return (
    text
      .replace(/^provincia\s+de\s+/i, '')
      .replace(/^província\s+de\s+/i, '')
      .replace(/^province\s+of\s+/i, '')
      .replace(/^province\s+de\s+/i, '')
      .trim() || text
  );
};

// Extract city (place) and province (region) from Mapbox feature context.
// Context items have id like "place.123" / "region.456" and text like "Mataró" / "provincia de Barcelona"
const extractCityAndProvince = prediction => {
  const context = prediction?.context;
  if (!Array.isArray(context)) {
    // Feature might be the place/region itself - use text
    const text = prediction?.text || '';
    const placeType = Array.isArray(prediction?.place_type) && prediction.place_type[0];
    if (placeType === 'place') return { city: text, province: '' };
    if (placeType === 'region') return { city: '', province: cleanProvinceText(text) };
    return { city: '', province: '' };
  }
  let city = '';
  let province = '';
  for (const ctx of context) {
    const id = ctx?.id || '';
    const text = (ctx?.text || '').trim();
    if (id.startsWith('place.')) city = text;
    else if (id.startsWith('region.')) province = cleanProvinceText(text);
  }
  return { city, province };
};

/**
 * A forward geocoding (place name -> coordinates) implementation
 * using the Mapbox Geocoding API.
 */
class GeocoderMapbox {
  getClient() {
    const libLoaded = typeof window !== 'undefined' && window.mapboxgl && window.mapboxSdk;
    if (!libLoaded) {
      throw new Error('Mapbox libraries are required for GeocoderMapbox');
    }
    if (!this._client) {
      this._client = window.mapboxSdk({
        accessToken: window.mapboxgl.accessToken,
      });
    }
    return this._client;
  }

  // Public API
  //

  /**
   * Search places with the given name.
   *
   * @param {String} search query for place names
   *
   * @return {Promise<{ search: String, predictions: Array<Object>}>}
   * results of the geocoding, should have the original search query
   * and an array of predictions. The format of the predictions is
   * only relevant for the `getPlaceDetails` function below.
   */
  getPlacePredictions(search, countryLimit, locale) {
    const limitCountriesMaybe = countryLimit ? { countries: countryLimit } : {};
    const pathName = window && window.location.pathname;
    const pathNameArray = pathName.split('/');
    const types =
      Array.isArray(pathNameArray) && pathNameArray.length > 5
        ? ['address']
        : [
            'address',
            'poi',
            'neighborhood',
            'locality',
            'place',
            'postcode',
            'country',
            'district',
          ];

    // Cache key includes locale + country limit + types so different callers
    // (topbar vs. listing wizard) don't share incompatible entries.
    const cacheKey = [
      normalizeCacheKey(search),
      locale || '',
      (countryLimit || []).join(','),
      types.join(','),
    ].join('|');
    const cachedFeatures = getCachedGeocode(PLACE_PREDICTIONS_CACHE_NAMESPACE, cacheKey);
    if (cachedFeatures) {
      return Promise.resolve({ search, predictions: cachedFeatures });
    }

    return this.getClient()
      .geocoding.forwardGeocode({
        query: search,
        limit: 5,
        ...limitCountriesMaybe,
        language: [locale],
        types,
      })
      .send()
      .then(response => {
        const features = response.body.features || [];
        setCachedGeocode(PLACE_PREDICTIONS_CACHE_NAMESPACE, cacheKey, features);
        return { search, predictions: features };
      });
  }

  /**
   * Get the ID of the given prediction.
   */
  getPredictionId(prediction) {
    return prediction.id;
  }

  /**
   * Get the address text of the given prediction.
   */
  getPredictionAddress(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.predictionPlace.address;
    }
    // prediction from Mapbox geocoding API
    return prediction.place_name;
  }

  /**
   * Fetch or read place details from the selected prediction.
   *
   * @param {Object} prediction selected prediction object
   *
   * @return {Promise<util.propTypes.place>} a place object
   */
  getPlaceDetails(prediction, currentLocationBoundsDistance) {
    if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
      return userLocation().then(latlng => {
        return {
          address: '',
          origin: latlng,
          bounds: locationBounds(latlng, currentLocationBoundsDistance),
        };
      });
    }

    if (prediction.predictionPlace) {
      return Promise.resolve(prediction.predictionPlace);
    }

    const { city, province } = extractCityAndProvince(prediction);
    return Promise.resolve({
      address: this.getPredictionAddress(prediction),
      origin: placeOrigin(prediction),
      bounds: placeBounds(prediction),
      city,
      province,
    });
  }
}

export default GeocoderMapbox;
